/**
 * Display dates: the input accepts/renders `dd.mm.yyyy`, the store keeps ISO
 * `YYYY-MM-DD` (the `dueDate` wire format, F-04).
 *
 * F-13b: a native `<input type="date">` paints a `dd.mm.yyyy` ghost text into
 * an empty field in Chrome — so the picker is a small controlled text input
 * whose placeholder stays EMPTY; the format lives in the field's label/title.
 */

/** Empty-string result means "the field was cleared" (stored as null). */
export type ParsedDate = string | null;

/** `YYYY-MM-DD` (or ISO datetime) → `dd.mm.yyyy`; empty in, empty out. */
export function formatDisplayDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = iso.slice(0, 10);
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!parts) return '';
  return `${parts[3]}.${parts[2]}.${parts[1]}`;
}

function isRealDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const february = leap ? 29 : 28;
  const daysInMonth = [31, february, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 0;
  return day <= daysInMonth;
}

/**
 * `dd.mm.yyyy` → `YYYY-MM-DD`. Dots are the only separator (as displayed),
 * single digits are accepted, whitespace is trimmed. Empty → null (cleared),
 * anything unparsable or not a real calendar date → undefined (invalid input,
 * the caller keeps the text and flags it).
 */
export function parseDisplayDate(raw: string): ParsedDate | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(trimmed);
  if (!match) return undefined;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (!isRealDate(year, month, day)) return undefined;
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Strict variant for validation display: `dd.mm.yyyy` fully parsed or nothing. */
export function isValidDisplayDate(raw: string): boolean {
  return parseDisplayDate(raw) !== undefined;
}

/** `YYYY-MM-DD` → local-midnight timestamp; null/invalid → null. */
export function displayDateToTimestamp(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.slice(0, 10));
  if (!match) return null;
  const timestamp = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(timestamp) ? null : timestamp;
}

/**
 * ISO `YYYY-MM-DD` → a LOCAL calendar `Date` (F-26: the DayPicker month grid
 * compares against the browser's "today" and renders day numbers, so the
 * date must be built in local time — the `Date.UTC` variant above is only a
 * timestamp). Unparsable/empty → undefined.
 */
export function isoToCalendarDate(iso: string | null | undefined): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso?.slice(0, 10) ?? '');
  if (!match) return undefined;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** Local calendar `Date` → `YYYY-MM-DD` (the inverse of `isoToCalendarDate`). */
export function calendarDateToIso(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Today's date as `YYYY-MM-DD` (UTC-stable, so the label never flaps per TZ). */
export function todayIso(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}`;
}

/**
 * ISO datetime → `dd.mm.yyyy, hh:mm` (ru-RU) — the audit-timestamp format.
 * Unparsable input is echoed as-is (seed data may carry odd ts strings).
 */
export function formatDateTime(ts: string): string {
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
